import { Component } from 'preact';
import { toast } from 'sonner';
import { UI } from '../utils/arena/uiSounds';
import { getDraftEvent, getDraftStandings, subscribeToDraftEvents, claimDraftMatchForfeit, syncDraftReadyPresence } from '../utils/arena/draftApi';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  PANEL_STYLE, BEVELED_BTN, GOLD_BTN,
  OrnamentalDivider, RADIUS,
} from '../lib/medievalTheme';
import { t } from '../localization/t';
import { isFreePlay } from '../utils/freePlay';
import AmbientParticles from './AmbientParticles';
import { BackButton } from './ui/navButtons';

export default class DraftTournament extends Component {
  constructor(props) {
    super(props);
    this.state = {
      standings: [],
      pairings: [],
      currentRound: 1,
      totalRounds: 1,
      myMatchRoomId: null,
      prizeCard: null,
      loading: true,
    };
  }

  componentDidMount() {
    this.unsubDraft = subscribeToDraftEvents({
      round_start: (data) => {
        this.setState({
          pairings: data.pairings || [],
          currentRound: data.round,
          totalRounds: data.totalRounds,
          myMatchRoomId: data.myRoomId || null,
        });
        this.loadStandings();
      },
      round_result: (data) => {
        this.setState((s) => ({ pairings: data.pairings || s.pairings }));
        this.loadStandings();
      },
      complete: (data) => {
        this.props.onDraftComplete?.(data);
      },
    });
    this.loadStandings();
    // Presence auto-prompt: if our opponent is already waiting at our
    // current match, ask the server to pull us into the ready-check so we
    // don't have to click Play Match to discover they're there.
    syncDraftReadyPresence(this.props.eventId).catch(() => {});
  }

  componentWillUnmount() {
    this.unsubDraft?.();
  }

  loadStandings = async () => {
    try {
      const [data, event] = await Promise.all([
        getDraftStandings(this.props.eventId),
        this.state.prizeCard ? null : getDraftEvent(this.props.eventId),
      ]);
      const pairings = data.currentPairings || this.state.pairings;

      // Extract myRoomId from pairings if not set
      let myRoomId = this.state.myMatchRoomId;
      if (!myRoomId && pairings.length > 0) {
        const myPairing = pairings.find(
          (p) => p.player1Id === this.props.profile?.id || p.player2Id === this.props.profile?.id
        );
        if (myPairing?.roomId) myRoomId = myPairing.roomId;
      }

      this.setState((s) => ({
        standings: data.standings || [],
        pairings,
        currentRound: data.currentRound || s.currentRound,
        totalRounds: data.totalRounds || s.totalRounds,
        myMatchRoomId: myRoomId || s.myMatchRoomId,
        prizeCard: event?.prizeCard || s.prizeCard,
        loading: false,
      }));
    } catch (err) {
      console.error('[DraftTournament] loadStandings failed:', err);
      this.setState({ loading: false });
    }
  };

  // "I'm willing, my opponent isn't." Attempt a forfeit claim; the server is
  // the source of truth. A no-show win is only valid once your opponent has
  // failed to show by the round deadline (a real ~24h absence), so a recent
  // match correctly refuses. Map the reason to a friendly toast. On success
  // the WS round_result / round_start refreshes the board.
  handleClaimForfeit = async (matchId) => {
    try {
      this.setState({ claimingMatchId: matchId });
      await claimDraftMatchForfeit(this.props.eventId, matchId);
      toast.success(t('Win claimed. Your opponent was a no-show.'));
    } catch (err) {
      const reason = err?.message || '';
      if (/claimant_not_ready/.test(reason)) {
        toast.error(t('Enter the match first, then claim if your opponent never shows.'));
      } else if (/opponent_present/.test(reason)) {
        toast.error(t('Your opponent has shown up. Play it out.'));
      } else if (/deadline_not_reached/.test(reason)) {
        toast.error(t('Too soon. You can only claim a win once your opponent fails to show by the round deadline.'));
      } else if (/match_complete/.test(reason)) {
        toast.message(t('This match is already resolved.'));
      } else {
        toast.error(t('Could not claim the win right now.'));
      }
    } finally {
      this.setState({ claimingMatchId: null });
    }
  };

  render() {
    const { profile, onPlayMatch, mode } = this.props;
    const { standings, pairings, currentRound, totalRounds, myMatchRoomId, loading } = this.state;
    const modeLabel = mode === 'sealed' ? t('Sealed') : t('Draft');

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden select-none" style={{ background: '#08080a' }}>
        <div className="absolute inset-0" style={{ background: `url('/hub-bg.webp') center/cover no-repeat`, filter: 'blur(6px)', transform: 'scale(1.02)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8) 100%)' }} />
        <AmbientParticles />

        {/* Floating back button. Without it, a user who hits Exit
            Game during a round match bounces back here and gets
            trapped with no navigation. Mirrors DraftResults. */}
        {this.props.onBack ? (
          <div className="absolute top-4 left-4 z-20">
            <BackButton title={t('Return to Hub')} onClick={this.props.onBack} />
          </div>
        ) : null}

        <div className="relative z-10 flex-1 flex flex-col items-center overflow-hidden p-6">
          <div className="w-full max-w-[42cqw]">
            <h2 className="text-2xl font-bold arena-heading text-center mb-1" style={{ color: ACCENT_GOLD, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(200,160,60,0.15)' }}>
              {t('{mode} Tournament', { mode: modeLabel })}
            </h2>
            <div className="text-xs text-center mb-4" style={{ color: TEXT_MUTED }}>
              {currentRound >= 1 ? t('Round {round} of {total}. Swiss', { round: currentRound, total: totalRounds }) : t('Waiting to start')}
            </div>
            <OrnamentalDivider className="mb-5" />

            {/* Pre-round waiting room: a player who has submitted their deck
                lands here while others are still building. Never a blocking
                overlay. They can read the status and leave (top-left back). */}
            {!loading && pairings.length === 0 ? (
              <div className="text-center py-8 px-4">
                <div className="text-sm font-semibold mb-2" style={{ color: ACCENT_GOLD }}>{t('Deck submitted')}</div>
                <p className="text-xs" style={{ color: TEXT_BODY, lineHeight: 1.6 }}>
                  {t('Waiting for the other players to finish building. The tournament starts when everyone is ready. You can leave and return anytime from Play, Your Tournaments.')}
                </p>
              </div>
            ) : null}

            {/* Current pairings */}
            <div className="mb-6">
              <div className="text-xs font-semibold mb-2" style={{ color: TEXT_MUTED }}>
                {t('Round {round} Pairings', { round: currentRound })}
              </div>
              <div className="space-y-2">
                {pairings.map((pairing, i) => {
                  const isMyMatch = pairing.player1Id === profile?.id || pairing.player2Id === profile?.id;
                  const isBye = !pairing.player2Id;
                  // Live round_start events ship the match id as
                  // `matchId`; the /standings REST response ships
                  // it as `id` (raw row). Normalize so the resume
                  // path (which hydrates from standings) still
                  // renders the Play Match button.
                  const matchId = pairing.matchId || pairing.id;
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between px-4 py-3 rounded"
                      style={{
                        ...PANEL_STYLE,
                        border: isMyMatch ? `1px solid ${ACCENT_GOLD}` : PANEL_STYLE.border,
                        boxShadow: isMyMatch ? `0 0 8px rgba(212,168,67,0.15)` : PANEL_STYLE.boxShadow,
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <span
                          className="text-sm font-semibold"
                          style={{ color: pairing.winnerId === pairing.player1Id ? '#6dba6d' : TEXT_PRIMARY }}
                        >
                          {pairing.player1Name || t('Player')}
                        </span>
                        <span className="text-xs" style={{ color: TEXT_MUTED }}>{t('vs')}</span>
                        <span
                          className="text-sm font-semibold"
                          style={{ color: isBye ? TEXT_MUTED : pairing.winnerId === pairing.player2Id ? '#6dba6d' : TEXT_PRIMARY }}
                        >
                          {isBye ? t('BYE') : (pairing.player2Name || t('Player'))}
                        </span>
                      </div>
                      {pairing.status === 'complete' ? (
                        <span className="text-[0.625cqw] font-bold" style={{ color: '#6dba6d' }}>{t('Done')}</span>
                      ) : isMyMatch && !isBye && matchId ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-4 py-1.5 text-xs font-bold transition-all cursor-pointer"
                            style={GOLD_BTN}
                            data-sound={UI.CONFIRM}
                            onClick={() => onPlayMatch?.({ ...pairing, matchId })}
                          >
                            {pairing.status === 'in_progress' ? t('Continue Match') : t('Play Match')}
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1.5 text-[0.625cqw] font-semibold transition-all cursor-pointer"
                            style={{ ...BEVELED_BTN, color: TEXT_BODY }}
                            disabled={this.state.claimingMatchId === matchId}
                            title={t('Claim the win if your opponent never shows up')}
                            onClick={() => this.handleClaimForfeit(matchId)}
                          >
                            {this.state.claimingMatchId === matchId ? t('Claiming…') : t('No-show?')}
                          </button>
                        </div>
                      ) : !isMyMatch && pairing.status === 'in_progress' && pairing.roomId ? (
                        <button
                          type="button"
                          className="px-3 py-1 text-[0.625cqw] font-semibold transition-all cursor-pointer"
                          style={{ ...BEVELED_BTN, color: TEXT_BODY }}
                          data-sound={UI.CONFIRM}
                          onClick={() => this.props.onSpectate?.(pairing.roomId)}
                        >
                          {t('Watch')}
                        </button>
                      ) : !isMyMatch && pairing.status === 'in_progress' ? (
                        <span className="text-[0.625cqw] font-bold animate-pulse" style={{ color: ACCENT_GOLD }}>{t('Playing')}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Prize teaser. Card identity hidden until draft ends */}
            {this.state.prizeCard && !isFreePlay() ? (
              <div className="flex items-center gap-3 mb-4 px-3 py-2 rounded" style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${GOLD} 0.15)` }}>
                <div
                  className="flex items-center justify-center text-lg shrink-0"
                  style={{ width: '2.75cqw', height: Math.round(44 * 88 / 63), borderRadius: RADIUS.card, background: `linear-gradient(135deg, rgba(212,168,67,0.15), rgba(180,140,60,0.05))`, border: `1px solid ${GOLD} 0.25)` }}
                >
                  ?
                </div>
                <div className="min-w-0">
                  <div className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('1st Place Prize')}</div>
                  <div className="text-sm font-semibold" style={{ color: ACCENT_GOLD }}>{t('Random Foil Card')}</div>
                  <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{t('Revealed at the end of the tournament')}</div>
                </div>
              </div>
            ) : null}

            {/* Standings table */}
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: TEXT_MUTED }}>
                {t('Standings')}
              </div>
              <div className="rounded overflow-hidden" style={PANEL_STYLE}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${GOLD} 0.15)` }}>
                      <th className="text-left px-3 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>#</th>
                      <th className="text-left px-3 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>{t('Player')}</th>
                      <th className="text-center px-3 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>W</th>
                      <th className="text-center px-3 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((entry, i) => {
                      const isMe = entry.playerId === profile?.id;
                      return (
                        <tr
                          key={entry.playerId}
                          style={{
                            background: isMe ? 'rgba(212,168,67,0.06)' : 'transparent',
                            borderBottom: `1px solid ${GOLD} 0.06)`,
                          }}
                        >
                          <td className="px-3 py-2 tabular-nums" style={{ color: i === 0 ? ACCENT_GOLD : TEXT_MUTED }}>{i + 1}</td>
                          <td className="px-3 py-2 font-semibold" style={{ color: isMe ? ACCENT_GOLD : TEXT_PRIMARY }}>{entry.playerName}</td>
                          <td className="px-3 py-2 text-center tabular-nums" style={{ color: '#6dba6d' }}>{entry.wins}</td>
                          <td className="px-3 py-2 text-center tabular-nums" style={{ color: '#c45050' }}>{entry.losses}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
